import { useEffect, useRef, useState } from 'react'

import { monthBounds } from '../lib/format'

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]

interface Props {
  from?: string
  to?: string
  onChange: (from: string | undefined, to: string | undefined) => void
}

/** Выбор периода: год сверху, под ним двенадцать месяцев. */
export default function PeriodPicker({ from, to, onChange }: Props) {
  const now = new Date()
  const [open, setOpen] = useState(false)
  // Год, который листаем в окне. Открывается на годе выбранного периода.
  const [year, setYear] = useState(() => (from ? Number(from.slice(0, 4)) : now.getFullYear()))
  const box = useRef<HTMLDivElement>(null)

  const selectedYear = from ? Number(from.slice(0, 4)) : null
  const selectedMonth = from ? Number(from.slice(5, 7)) - 1 : null

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  function pick(monthIndex: number) {
    const { from: f, to: t } = monthBounds(new Date(year, monthIndex, 1))
    onChange(f, t)
    setOpen(false)
  }

  const label =
    selectedYear !== null && selectedMonth !== null && isWholeMonth(from!, to)
      ? `${MONTHS[selectedMonth]} ${selectedYear}`
      : from || to
        ? 'Свой период'
        : 'Все даты'

  return (
    <div className="period-picker" ref={box}>
      <button type="button" className="btn period-button" onClick={() => setOpen(!open)}>
        {label}
        <span className="period-caret">▾</span>
      </button>

      {open && (
        <div className="period-popup">
          <div className="period-year">
            <button type="button" className="btn small" onClick={() => setYear(year - 1)}>
              ‹
            </button>
            <span className="period-year-value">{year}</span>
            <button type="button" className="btn small" onClick={() => setYear(year + 1)}>
              ›
            </button>
          </div>

          <div className="period-months">
            {MONTHS.map((name, i) => {
              const isCurrent = year === now.getFullYear() && i === now.getMonth()
              const isSelected = year === selectedYear && i === selectedMonth
              return (
                <button
                  key={name}
                  type="button"
                  className={`period-month${isSelected ? ' selected' : ''}${
                    isCurrent ? ' current' : ''
                  }`}
                  onClick={() => pick(i)}
                >
                  {name}
                </button>
              )
            })}
          </div>

          {/* Произвольный отрезок живёт здесь же: в панели фильтров два лишних
              поля с датами занимали целый ряд и дублировали выбор месяца */}
          <div className="period-custom">
            <div className="period-custom-title">Свой период</div>
            <div className="period-custom-row">
              <input
                type="date"
                value={from ?? ''}
                onChange={(e) => onChange(e.target.value || undefined, to)}
              />
              <span>—</span>
              <input
                type="date"
                value={to ?? ''}
                onChange={(e) => onChange(from, e.target.value || undefined)}
              />
            </div>
          </div>

          <button
            type="button"
            className="btn small period-all"
            onClick={() => {
              onChange(undefined, undefined)
              setOpen(false)
            }}
          >
            Сбросить период
          </button>
        </div>
      )}
    </div>
  )
}

/** Период ровно в один календарный месяц — тогда показываем его название. */
function isWholeMonth(from: string, to?: string): boolean {
  if (!to) return false
  const bounds = monthBounds(new Date(`${from}T00:00:00`))
  return bounds.from === from && bounds.to === to
}
